/**
 * Table Empty state component
 */

import * as React from 'react';
import { TableRow, TableCell } from './table';
import { Icon, type IconName } from './icon';
import { cn } from '@/lib/utils';

export interface TableEmptyProps {
  icon?: IconName;
  title?: string;
  message?: string;
  description?: string;
  action?: React.ReactNode;
  colSpan?: number;
  className?: string;
}

/**
 * TableEmpty can be used either as a standalone div or inside a TableRow
 */
export function TableEmpty({
  icon = 'file-text',
  title,
  message,
  description,
  action,
  colSpan,
  className,
}: TableEmptyProps) {
  const displayText = title || message || 'No items found';
  
  const content = (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 text-center',
        className
      )}
    >
      <div className="rounded-full bg-muted p-4">
        <Icon name={icon} className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">{displayText}</h3>
      {description && (
        <p className="mt-2 text-sm text-muted-foreground max-w-sm">
          {description}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );

  // If colSpan is provided, wrap in TableRow/TableCell for use in tables
  if (colSpan !== undefined) {
    return (
      <TableRow>
        <TableCell colSpan={colSpan} className="h-24">
          {content}
        </TableCell>
      </TableRow>
    );
  }

  return content;
}
